/* -----------------------------------------------------------------------
Solutions Template
-----------------------------------------------------------------------
Links (ctrl + click to follow):
Contentful Entries: TBD
Content Model: TBD
GraphQL Explorer: TBD
-----------------------------------------------------------------------
Supported components: TBD
Template Sections: Custom Components, Page Elements, Solutions Page Template
Slug: /solutions/slug
-------------------------------------------------------------------------*/

import React from 'react';
import styled from 'styled-components';
import { graphql } from 'gatsby';
import Slider from 'react-slick';

import Layout from '@layout';
import Helmet from '@basic/Helmet';

import TwoColumnHero from '@basic/hero/TwoColumnHero';
import Text from '@basic/Text';
import HTMLWrapper from '@basic/HTMLWrapper';
import { Grid, Cell } from '@basic/layouts/Grid';
import SingleImageBlock from '@basic/image-block/SingleImageBlock';
import SectionScroll from '@basic/SectionScroll';
import Section from '@basic/layouts/Section';
import ListOfLogos from '@basic/ListOfLogos';
// import LargeScreenshotAccordion from '@basic/accordion/LargeScreenshotAccordion';
import BasicQuoteSlide from '@components/Slider/BasicQuoteSlide';
import Button from '../components/basic/BasicButton';
import WistiaVideo from '../components/basic/WistiaVideo';
import CTA from '@basic/BasicCTA';
import BasicAccordion from '@basic/accordion/BasicAccordion';

import SolutionsNavBG from '@images/custom/solutions/SolutionsNavBG.svg';
import FiveStars from '@images/icons/software-reviews/5Stars.svg';
import FourStars from '@images/icons/software-reviews/FourStars.svg';
import Capterra from '@images/icons/software-reviews/Capterra.svg';
import SurveyMonkey from '@images/icons/software-reviews/SurveyMonkey.svg';
import OverflowScreenshotAccordion from '../components/basic/accordion/OverflowScreenshotAccordion';

import theme from '@theme';
import Link from '../components/util/link';

const {
	font,
	colors,
	breakPoints: { mobileFirstScreenQuery },
} = theme;

/* -----------------------------------------------------------------------
Custom Page Elements
-------------------------------------------------------------------------*/

/* --- SolutionsSectionWrapper ---*/

const SolutionsSectionWrapper = styled(Section)`
	h2 {
		font-size: ${({ headerSize }) => `${headerSize ? headerSize : 32}px`};
		line-height: 44px; /* 137.5% */
	}

	.section-subheader p {
		font-size: 18px;
		font-style: normal;
		line-height: 28px; /* 155.556% */
		text-wrap: balance;
	}

	${mobileFirstScreenQuery.medium} {
		padding: ${props => (props.noPadding ? '0' : '144px 0')};
	}
`;

const SolutionsSection = ({
	children,
	headerSize,
	maxWidth,
	noPadding,
	...props
}) => {
	return (
		<SolutionsSectionWrapper
			maxWidth={maxWidth ? `${maxWidth}px` : '1110px'}
			headerSize={headerSize}
			noPadding={noPadding}
			{...props}
		>
			{children}
		</SolutionsSectionWrapper>
	);
};

/* -----------------------------------------------------------------------
PAGE SECTIONS
-------------------------------------------------------------------------*/

/* --- SolutionsHero ---*/

const SolutionsHero = ({ content }) => {
	const heroMedia = content.heroFeatureMedia;
	// Add if we get a static image hero
	// const heroImage = heroMedia?.image?.file?.url;
	const heroVideo = heroMedia?.videoLink;
	const heroLinkedImage = heroMedia?.imageUrl;
	const heroThumbnail = heroMedia?.videoThumbnail;

	return (
		<TwoColumnHero
			maxWidth="1110px"
			complexGradient={`radial-gradient(100% 44% at 10% 50%, #0FB8C64F 1%, #073AFF00 83%),linear-gradient(37deg, #789EE842 29%, #fff 100%)`}
			header={content.heroHeadline}
			subheader={content.heroSubhead}
			buttons={content.heroCtaButtons}
			linkHero={heroLinkedImage && heroMedia}
			heroVideo={heroVideo}
			thumbnail={heroThumbnail}
			lightBox
			noAutoplay={heroVideo && true}
			withPlayButton
			customPadding="100% 0 0 0"
			additionalComponent={
				content.heroCapterraRating && (
					<StarRating
						rating={content.heroCapterraRating}
						ratingSource={content.heroRatingSource}
						url={content.heroRatingUrl}
					/>
				)
			}
		/>
	);
};

/* --- SolutionsLargeScreenshotAccordion ---*/

const SolutionsLargeScreenshotAccordion = styled(OverflowScreenshotAccordion)`
	padding-top: 0;
	a {
		display: block;
		padding: 10px 0;
		width: fit-content;
		font-family: ${font.semiBold};
		background: linear-gradient(${colors.brandMain}, ${colors.brandMain});
		background-size: 0 2px;
		background-position: 0 80%;
		background-repeat: no-repeat;
		transition:
			background-size 0.75s ease-in-out,
			color 0.5s ease-in;
		color: ${colors.newBrand.black};

		&:hover {
			background-size: 100% 2px;
			color: ${colors.brandMain};
		}
	}
`;

/* --- SolutionsStatLine ---*/

const SolutionsStatLineWrapper = styled.div`
	margin: 2.5rem auto;
	display: flex;
	justify-content: center;
	flex-direction: column;

	div {
		display: flex;
		flex-direction: column;
		text-align: center;
		padding: 2rem 0;
		margin: 0 auto;
		width: 60%;
	}

	div:not(:last-child) {
		border-bottom: 1px solid rgba(138, 146, 162, 0.45);
	}

	h3 {
		font-size: 40px;
		letter-spacing: 0.4px;
	}

	h3,
	p {
		color: ${({ darkBlock }) => (darkBlock ? '#fff' : '#333')};
	}

	p {
		text-wrap: pretty;
	}

	${mobileFirstScreenQuery.large} {
		margin: ${({ padding }) => padding} auto;
		max-width: ${width => width};
		display: grid;
		grid-template-columns: repeat(${({ length }) => length}, 1fr);
		grid-template-rows: 1fr;
		grid-column-gap: 0px;
		grid-row-gap: 0px;

		div {
			display: block;

			padding: 0;
			width: 100%;
		}

		> div {
			padding: 0 40px;
		}

		div:not(:last-child) {
			border-bottom: none;
			border-right: 1px solid rgba(138, 146, 162, 0.45);
		}
	}
`;

const AdditionalButton = styled(Button)`
    margin: ${({ buttonMargin }) => buttonMargin} auto;
    display: flex;
    justify-content: center;
}`;

const SolutionsStatLine = ({ stats, padding, width, darkBlock }) => {
	return (
		<SolutionsStatLineWrapper
			padding={padding}
			width={width}
			length={stats.length}
			darkBlock={darkBlock}
		>
			{stats.map(stat => {
				return (
					<div key={`${stat.statistic}-stat`}>
						<Text component="h3">
							{stat.statistic} {stat.statisticSubdescription}
						</Text>
						{stat.statisticDescription && (
							<HTMLWrapper
								html={stat.statisticDescription.childMarkdownRemark.html}
							/>
						)}
					</div>
				);
			})}
		</SolutionsStatLineWrapper>
	);
};

/* --- LogoSlider ---*/

const LogoSliderWrapper = styled.div`
	.slick-track {
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.logo__img-wrapper img {
		margin: 0 auto;
		filter: invert(46%) sepia(9%) saturate(264%) hue-rotate(176deg)
			brightness(92%) contrast(86%);
	}

	${mobileFirstScreenQuery.large} {
		/* Fading edges */
		.slick-list::before,
		.slick-list::after {
			content: '';
			position: absolute;
			top: 0;
			bottom: 0;
			width: 400px; /* Adjust width for the fade area */
			pointer-events: none; /* Allows clicks to pass through */
			z-index: 1;
		}

		/* Left fade */
		.slick-list::before {
			left: 0;
			background: linear-gradient(
				to right,
				rgba(255, 255, 255, 1),
				rgba(255, 255, 255, 0)
			);
		}

		/* Right fade */
		.slick-list::after {
			right: 0;
			background: linear-gradient(
				to left,
				rgba(255, 255, 255, 1),
				rgba(255, 255, 255, 0)
			);
		}
	}
`;

const LogoSlider = ({ content }) => {
	const slidesToShow = content.logos.length < 8 ? content.logos.length : 8;
	var settings = {
		dots: false,
		infinite: true,
		slidesToShow: slidesToShow,
		slidesToScroll: 1,
		autoplay: true,
		speed: 5000,
		autoplaySpeed: 5000,
		cssEase: 'linear',
		pauseOnHover: true,
		responsive: [
			{
				breakpoint: 1500,
				settings: {
					slidesToShow: 4,
					slidesToScroll: 1,
				},
			},
			{
				breakpoint: 600,
				settings: {
					slidesToShow: 3,
					slidesToScroll: 1,
				},
			},
			{
				breakpoint: 480,
				settings: {
					slidesToShow: 2,
					slidesToScroll: 1,
				},
			},
		],
	};
	return (
		<LogoSliderWrapper className="slider-container">
			<Slider {...settings} className="logo__slider">
				{content.logos.map(logo => {
					return (
						<div className="logo__img-wrapper">
							<img src={logo.file.url} />
						</div>
					);
				})}
			</Slider>
		</LogoSliderWrapper>
	);
};

/* --- StarRating ---*/

const RatingLink = styled(Link)`
	:hover {
		cursor: pointer;
		color: ${colors.brandMain};
	}
`;

const RatingWrapper = styled.div`
	display: flex;
	gap: 10px;
	align-items: center;

	img {
		max-height: 24px;
	}

	div {
		font-family: ${theme.font.bold};
		font-size: 20px;
		line-height: 100%; /* 20px */
		letter-spacing: 3px;
		text-transform: uppercase;
	}
`;

const StarRating = ({ rating, ratingSource, url }) => {
	const stars = rating < 4.9 ? FourStars : FiveStars;
	return url ? (
		<RatingLink to={url}>
			<RatingWrapper>
				<div>{rating} Rating</div>
				<img src={stars} alt={`${rating} stars`} />
				<img
					src={ratingSource === 'Survey Monkey' ? SurveyMonkey : Capterra}
					alt={`${ratingSource} logo`}
				/>
			</RatingWrapper>
		</RatingLink>
	) : (
		<RatingWrapper>
			<div>{rating} Rating</div>
			<img src={stars} alt={`${rating} stars`} />
			<img
				src={ratingSource === 'Survey Monkey' ? SurveyMonkey : Capterra}
				alt={`${ratingSource} logo`}
			/>
		</RatingWrapper>
	);
};

/* -----------------------------------------------------------------------
Section Components
-------------------------------------------------------------------------*/

/* --- SolutionsValidationSection ---*/

const SolutionsValidationSectionWrapper = styled(Section)`
	width: 100%;
	max-width: 100%;
	padding: 40px 0;

	${mobileFirstScreenQuery.large} {
		padding: 80px 0 144px;
	}
`;

const SolutionsValidationSection = ({
	awards,
	stats,
	customers,
	screenshots,
}) => {
	return (
		<>
			<SolutionsValidationSectionWrapper>
				{awards && (
					<ListOfLogos noBottomPadding noTopPadding logos={awards.logos} />
				)}
				{stats && (
					<SolutionsStatLine padding="40px" width={600} stats={stats} />
				)}
				{customers && <LogoSlider content={customers} />}
			</SolutionsValidationSectionWrapper>
			<SolutionsLargeScreenshotAccordion
				content={screenshots}
				screenshotArea={883}
			/>
		</>
	);
};

/* --- SolutionSingleImageBlock ---*/

const SolutionSingleImageBlockWrapper = styled(SingleImageBlock)`
	.image-block-overline {
		border-radius: 8px;
		background: rgba(120, 158, 232, 0.25);
		color: #fff;
		font-size: 12px;
		letter-spacing: 2.4px;
		text-transform: uppercase;
		padding: 16px 20px;
		text-align: center;
		width: fit-content;
		margin-bottom: 32px;
	}

	h2 {
		color: #fff;
		font-size: 32px;
		line-height: 44px; /* 137.5% */
	}

	p {
		color: #fff;
		font-size: 18px;
		line-height: 28px; /* 155.556% */
		letter-spacing: 0.18px;
	}

	.single-image-block__button {
		background: linear-gradient(white, white);
		background-size: 0 2px;
		background-position: 0 100%;
		background-repeat: no-repeat;
		transition: all 0.25s ease-in;
		color: white;

		.link-arrow {
			transition: all 0.25s ease-in;
			stroke: white;
		}

		:hover {
			background-size: 100% 2px;
			color: white;

			.link-arrow {
				stroke: white;
			}
		}
	}
`;

const SolutionSingleImageBlock = ({ index, content, stats }) => {
	return (
		<SolutionsSection
			backgroundColor="#131E33"
			noBottomPadding={stats || index === 2 ? true : false}
		>
			<SolutionSingleImageBlockWrapper
				flipX={index % 2 === 0}
				header={content?.textblockHeader}
				raw={content?.textSectionNew}
				headerHead={content?.headerHeadForClPs}
				image={content?.image?.file.url}
				additionalLinks={content?.additionalLinks}
			/>
			{stats && <SolutionsStatLine padding="80px" darkBlock stats={stats} />}
		</SolutionsSection>
	);
};

/* --- SolutionsTestimonial ---*/

const SolutionsTestimonial = ({ content, hasImageBlock }) => {
	const logos = content?.quotes?.map(item =>
		item.quoteAuthor
			? item.quoteAuthor.authorCompanyLogo
			: item.sourceCompanyLogo
				? item.sourceCompanyLogo
				: null
	);

	const filteredLogos = logos.filter(logo => logo != null);

	return (
		<SolutionsSection noPadding={!hasImageBlock}>
			<Grid>
				<Cell span={10} start={2} gridGap="0px">
					<BasicQuoteSlide
						testimonialList={content.quotes}
						quoteType="logoQuote"
					/>
					<ListOfLogos noBottomPadding logos={filteredLogos} />
				</Cell>
			</Grid>
		</SolutionsSection>
	);
};

/* --- SpecialtySection ---*/

const SpecialtySection = ({ content }) => {
	const sectionContent = content.content;
	const url = sectionContent?.videoLink;
	return (
		<SolutionsSection
			header={content.headline}
			subheaderHTML={content.subhead.childMarkdownRemark.html}
			centerHeader
			maxWidth={841}
		>
			{sectionContent?.type === 'ContentfulElementBasicVideo' && (
				<WistiaVideo
					videoId={url.slice(
						url.lastIndexOf('/') + 1,
						url.lastIndexOf('.') > url.lastIndexOf('/')
							? url.lastIndexOf('.')
							: url.length
					)}
					thumbnail={sectionContent?.videoThumbnail?.file.url}
				/>
			)}
			{sectionContent?.type === 'ContentfulElementBasicImage' && (
				<img src={sectionContent.image.file.url} style={{ width: '100%' }} />
			)}
			{sectionContent?.type === 'ContentfulSectionListOfLogos' && (
				<ListOfLogos
					noTopPadding
					noBottomPadding
					cardStyle
					logos={sectionContent.logos}
				/>
			)}
			{content?.cta && (
				<AdditionalButton to={content.cta.linkUrl} buttonMargin={'40px'}>
					{content.cta.linkText}
				</AdditionalButton>
			)}
		</SolutionsSection>
	);
};

/* --- SolutionScrollSection ---*/

const SolutionScrollSection = ({
	content,
	index,
	stats,
	additionalContent,
}) => {
	return (
		<>
			{index === 0 ? (
				<SolutionsValidationSection
					awards={content.awards}
					stats={content.stats}
					customers={content.customers}
					screenshots={content.screenshots}
				/>
			) : (
				<>
					{content && (
						<SolutionSingleImageBlock
							content={content}
							stats={stats}
							index={index}
						/>
					)}
					{additionalContent?.type === 'quotes' && (
						<SolutionsTestimonial
							content={additionalContent}
							hasImageBlock={content}
						/>
					)}
					{additionalContent?.type === 'specialty' && (
						<SpecialtySection content={additionalContent} />
					)}
				</>
			)}
		</>
	);
};

/* --- SolutionsScroll ---*/

const SolutionsScrollWrapper = styled.div`
	// TODO Transfer these styles to page-wide styles
	.section-scroll__nav-container {
		h5 {
			padding-top: 40px;
			text-align: center;
			margin-bottom: 0;
			color: #f3f1e6;
			font-size: 14px;
			line-height: 16px; /* 114.286% */
			letter-spacing: 2.8px;
			text-transform: uppercase;
		}

		a {
			color: rgba(243, 241, 230, 0.45);
			.active {
				color: #f3f1e6;
			}
		}
	}

	.section-scroll__nav {
		padding-top: 16px;
		padding-bottom: 0;
		display: flex;
		gap: 32px;
		max-width: 100vw;

		${mobileFirstScreenQuery.extraLarge} {
			max-width: 85vw;
		}
	}

	.section-scroll__nav-text {
		color: rgba(243, 241, 230, 0.45);
		font-size: 16px;
		line-height: 16px; /* 100% */
		letter-spacing: 0.16px;
		padding: 16px 0 24px;
		text-transform: capitalize;

		&:hover {
			color: #f3f1e6;
		}
	}
`;

export const SolutionsScroll = ({ sections, navTitle }) => {
	return (
		<SolutionsScrollWrapper>
			<SectionScroll
				backgroundImg={SolutionsNavBG}
				navHeadline={navTitle}
				sections={sections}
				noMobileNav
			/>
		</SolutionsScrollWrapper>
	);
};

/* --- SolutionsFAQ ---*/

const SolutionsFAQWrapper = styled(BasicAccordion)`
	h3 {
		color: #333;
		font-family: ${font.semiBold};
		font-size: 20px;
		line-height: 24px; /* 120% */
		letter-spacing: 0.2px;
	}
`;

const SolutionsFAQ = ({ header, content }) => {
	return (
		<SolutionsSection
			complexGradient={`
            	radial-gradient(40% 30% at 10% 90%, #FC5B301A 0%, #FFFFFF00 100%),
				radial-gradient(100% 40% at 0% 50%, #FC5B301A 0%, #FFFFFF00 100%),
				radial-gradient(20% 40% at 80% 40%, #FC5B301A 0%, #FFFFFF00 100%),
				radial-gradient(60% 60% at 80% 80%, #789EE854 0%, #FFFFFF00 100%),
				linear-gradient(90deg, #789EE854 2%, #fff 83%)
			`}
			header={header}
			headerSize={48}
			centerHeader
		>
			<SolutionsFAQWrapper headerPadding="40px 0" questionSet={content} />
		</SolutionsSection>
	);
};

/* --- SolutionsAdditionalResources ---*/

const SolutionsAdditionalResources = ({ headline, subhead, cta, content }) => {
	return (
		<SolutionsSection
			header={headline}
			subheaderHTML={subhead.childMarkdownRemark.html}
			centerHeader
			maxWidth={900}
			noHeaderPadding
		>
			<AdditionalButton to={cta.linkUrl} buttonMargin="24px">
				{cta.linkText}
			</AdditionalButton>
			{content?.videoLink && (
				<WistiaVideo
					videoId={content.videoLink.slice(
						content.videoLink.lastIndexOf('/') + 1,
						content.videoLink.lastIndexOf('.') >
							content.videoLink.lastIndexOf('/')
							? content.videoLink.lastIndexOf('.')
							: content.videoLink.length
					)}
					thumbnail={content?.videoThumbnail?.file.url}
				/>
			)}
		</SolutionsSection>
	);
};

/* -----------------------------------------------------------------------
Solutions Page Template
-------------------------------------------------------------------------*/

const Page = ({ data, location }) => {
	const content = data.contentfulPageTemplateSolutions;

	const specialtySection = content.specialtySection_Headline && {
		type: 'specialty',
		headline: content.specialtySection_Headline,
		subhead: content.specialtySection_Subhead,
		cta: content.specialtySection_Cta,
		content: content.specialtySection_Content,
	};

	const proofQuotes = content.proof_Quotes && {
		type: 'quotes',
		quotes: content.proof_Quotes,
	};

	const scrollSectionContent = [
		{
			content: {
				awards: content.validationSection_AwardLogoLine,
				stats: content?.validationSection_Stats,
				customers: content.validationSection_CustomerLogoLine,
				screenshots: content.validationSection_Screenshots,
			},
		},
		{
			title: content.differentiator1_ScrollTitle,
			content: content.differentiator1_Content,
			stats: content.proofStats,
			additionalContent: { ...proofQuotes },
		},
		{
			title: content.differentiator2_ScrollTitle,
			content: content.differentiator2_Content,
		},
		{
			title: content.differentiator3_ScrollTitle,
			content: content.differentiator3_Content,
			additionalContent: { ...specialtySection },
		},
		{
			title: content.differentiator4_ScrollTitle,
			content: content.differentiator4_Content,
		},
	];

	const scrollSections = scrollSectionContent.map((section, index) => {
		const scrollTitle =
			index === 0 ? '' : section?.title || section?.content?.headerHeadForClPs;
		return {
			id: scrollTitle,
			content: (
				<SolutionScrollSection
					content={section.content}
					stats={section.stats}
					additionalContent={section.additionalContent}
					key={`section-${index}-${scrollTitle}`}
					index={index}
				/>
			),
		};
	});

	return (
		<Layout key="content" location={location}>
			<Helmet
				pageTitle={content.metaPageTitle}
				description={content.metaPageDescription}
				location={location}
				updatedAt={content.updatedAt}
				noIndex={!content.metaIsSearchable}
				metaKeywords={content.metaPageKeywords}
				key="new-solutions-page"
			/>
			<SolutionsHero content={content} />
			<SolutionsScroll
				sections={scrollSections}
				navTitle={content.scrollMeta_Title}
			/>
			<CTA
				header={content.ctaSection_Content.headline}
				subheader={
					content.ctaSection_Content.subhead?.childMarkdownRemark?.html
				}
				button={content.ctaSection_Content.button}
				bgGradient={
					'radial-gradient(75% 100% at 115% 25%, #0FB8C6 0%, #073AFF00 100%),radial-gradient(76% 100% at 45% 26%, #789EE8D1 0%, #073AFF00 100%),radial-gradient(142% 91% at 111% 84%, #131E33 0%, #131E33 100%)'
				}
			/>
			{content.faqSection_Content && (
				<SolutionsFAQ
					header={content.faqSection_Header}
					content={content.faqSection_Content}
				/>
			)}
			{content?.additionalResources_Headline && (
				<SolutionsAdditionalResources
					headline={content.additionalResources_Headline}
					subhead={content.additionalResources_Subhead}
					cta={content.additionalResources_Cta}
					content={content.additionalResources_Content}
				/>
			)}
		</Layout>
	);
};

export default Page;

export const newSolutionsQuery = graphql`
	query solutionsQuery($contentful_id: String!) {
		contentfulPageTemplateSolutions(contentful_id: { eq: $contentful_id }) {
			updatedAt
			metaPageTitle
			metaPageDescription
			metaIsSearchable
			metaPageKeywords
			scrollMeta_Title
			heroHeadline
			heroSubhead {
				childMarkdownRemark {
					html
				}
			}
			heroFeatureMedia {
				... on ContentfulElementBasicImage {
					type: __typename
					imageUrl
					image {
						file {
							url
						}
					}
				}
				... on ContentfulElementBasicVideo {
					type: __typename
					videoLink
					videoThumbnail {
						file {
							url
						}
					}
				}
			}
			heroCapterraRating
			heroRatingSource
			# heroRatingUrl
			heroCtaButtons {
				linkText
				linkUrl
			}
			validationSection_AwardLogoLine {
				...ListOfLogos
			}
			validationSection_CustomerLogoLine {
				...ListOfLogos
			}
			validationSection_Stats {
				... on ContentfulStatBlock {
					contentful_id
					statistic
					statisticSubdescription
					statisticDescription {
						childMarkdownRemark {
							html
						}
					}
				}
			}
			validationSection_Screenshots {
				...ScreenshotsFragment
			}
			differentiator1_ScrollTitle
			differentiator1_Content {
				...ImageBlockFragment
			}
			differentiator2_ScrollTitle
			differentiator2_Content {
				...ImageBlockFragment
			}
			differentiator3_ScrollTitle
			differentiator3_Content {
				...ImageBlockFragment
			}
			differentiator4_ScrollTitle
			differentiator4_Content {
				...ImageBlockFragment
			}
			proofStats {
				... on ContentfulStatBlock {
					contentful_id
					statistic
					statisticSubdescription
					statisticDescription {
						childMarkdownRemark {
							html
						}
					}
				}
			}
			proof_Quotes {
				... on ContentfulPhotoQuote {
					type: __typename
					quote {
						quote
						childMarkdownRemark {
							html
						}
					}
					customerStoryPage {
						slug
					}
					quoteAuthor {
						... on ContentfulElementQuoteAuthor {
							authorName
							authorJobTitle
							authorBioPhoto {
								file {
									url
								}
							}
							authorCompanyLogo {
								file {
									url
								}
							}
						}
					}
				}
			}
			specialtySection_Headline
			specialtySection_Subhead {
				childMarkdownRemark {
					html
				}
			}
			specialtySection_Content {
				... on ContentfulElementBasicVideo {
					type: __typename
					videoLink
					videoThumbnail {
						file {
							url
						}
					}
				}
				... on ContentfulElementBasicImage {
					type: __typename
					contentful_id
					altText
					image {
						file {
							url
						}
					}
				}
				... on ContentfulSectionListOfLogos {
					type: __typename
					logos {
						file {
							url
						}
					}
				}
			}
			specialtySection_Cta {
				... on ContentfulElementBasicLink {
					linkText
					linkUrl
				}
			}
			ctaSection_Content {
				...CTABlockFragment
			}
			faqSection_Header
			faqSection_Content {
				... on ContentfulBasicRichText {
					contentful_id
					title
					richText {
						raw
					}
				}
			}
			additionalResources_Headline
			additionalResources_Subhead {
				childMarkdownRemark {
					html
				}
			}
			additionalResources_Cta {
				... on ContentfulElementBasicLink {
					linkText
					linkUrl
				}
			}
			additionalResources_Content {
				... on ContentfulElementBasicVideo {
					type: __typename
					videoLink
					videoThumbnail {
						file {
							url
						}
					}
				}
			}
		}
	}
`;
